import { type KeyboardEventHandler, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import GenreTag from '~/features/circle-participation/components/GenreTag'
import {
  useApplyUserCircleBoothformPatch,
  useCircleBoothApplyDetail,
  useCircleBoothform,
} from '~/features/circle-participation/queries/useCircleApply'
import {
  type ComplexityType,
  type ExpType,
  type Goods,
  type Image,
  type ItemType,
  type SizeType,
} from '~/features/circle-participation/utils/type'
import { useFileUpload } from '~/features/file-upload/queries/useFileUpload'
import { useOngoingEvent } from '~/features/main/queries/useMainContents'
import Back from '~/shared/assets/icons/Back'
import Check from '~/shared/assets/icons/Check'
import ImageUpload from '~/shared/assets/icons/ImageUpload'
import Button from '~/shared/components/buttons/Button'
import Tag from '~/shared/components/tags/Tag'
import theme from '~/shared/styles/theme'

// TODO 이미지 삭제 추가
// TODO 코드가 더러워요..

interface IForm {
  representativeImage: string
  sizeType: keyof typeof SizeType
  complexityType: keyof typeof ComplexityType
  expType: keyof typeof ExpType
  goodsTypeList: ItemType[]
  goodsList: Goods[]
  genreTypeList: ItemType[]
  genreTags: string[]
  representationNickname: string
  introduce: string
  homepage: string
  nearBooth: string
  goodsInfoImage: string[]
  imageInfo: Image
}

function CircleApplyAddInfoPage() {
  const navigate = useNavigate()
  const { applicationId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const [uploadedCircleImage, setUploadedCircleImage] = useState<Image>()
  const [uploadedGoodsImageList, setUploadedGoodsImageList] = useState<Image[]>(
    [],
  )

  const { data: ongoingEvent } = useOngoingEvent()
  const { data: circleBoothform, refetch } = useCircleBoothform(
    ongoingEvent?.data.eventInfo.id.toString() || '',
  )

  const { data: circleBoothApplyDetail } = useCircleBoothApplyDetail(
    applicationId || '',
  )

  const { mutate: userCircleBoothformPatchMutate } =
    useApplyUserCircleBoothformPatch()
  const { mutate: fileUploadMutate } = useFileUpload()

  const { register, watch, handleSubmit, setValue } = useForm<IForm>({
    mode: 'onSubmit',
    defaultValues: {},
  })

  const [
    sizeType,
    goodsTypeList,
    genreTags,
    complexityType,
    expType,
    genreTypeList,
    imageInfo,
    goodsList,
  ] = watch([
    'sizeType',
    'goodsTypeList',
    'genreTags',
    'complexityType',
    'expType',
    'genreTypeList',
    'imageInfo',
    'goodsList',
  ])

  const checkFileSize = (file: File, maxSizeMB: number): boolean => {
    const MAX_FILE_SIZE = maxSizeMB * 1024 * 1024
    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar(`파일 크기는 ${maxSizeMB}MB를 초과할 수 없습니다.`, {
        variant: 'error',
      })

      return false
    }

    return true
  }

  const uploadCircleImageToServer = async (file: File) => {
    if (file) {
      if (!checkFileSize(file, 2)) return
      await fileUploadMutate(file, {
        onSuccess: (res) => {
          if (res.data.errorCode === 0) {
            const ImageData = {
              id: res.data.data.file_list[0].id,
              url: res.data.data.file_list[0].url,
            }

            setUploadedCircleImage(ImageData)
            enqueueSnackbar('파일 업로드가 완료되었습니다.', {
              variant: 'success',
            })
          } else if (res.data.errorCode < 0) {
            enqueueSnackbar(res.data.errorMsg, { variant: 'error' })
          }
        },
        onError: (error) => {
          enqueueSnackbar(error.message, { variant: 'error' })
        },
      })
    }
  }

  const uploadGoodsImageToServer = async (file: File) => {
    if (file) {
      if (!checkFileSize(file, 5)) return
      await fileUploadMutate(file, {
        onSuccess: (res) => {
          if (res.data.errorCode === 0) {
            const ImageData = {
              id: res.data.data.file_list[0].id,
              url: res.data.data.file_list[0].url,
            }

            setUploadedGoodsImageList([...uploadedGoodsImageList, ImageData])
          } else if (res.data.errorCode < 0) {
            enqueueSnackbar(res.data.errorMsg, { variant: 'error' })
          }
        },
      })
    }
  }

  // 추가정보일 때만 is_option을 Y로 보내주기

  const onSubmit: SubmitHandler<IForm> = (data) => {
    const userBoothApplication = {
      ...circleBoothApplyDetail?.data.userBoothApplication,
      is_option: 'Y',
      id: applicationId || '',
      size_type: sizeType,
      complexity_type: complexityType,
      exp_type: expType,
      goods_type: goodsTypeList.map((goods) => goods.code).join(),
      genre_type: genreTypeList.map((genre) => genre.code).join(),
      tag: genreTags.join(),
      user_nickname: data.representationNickname,
      introduce: data.introduce,
      homepage: data.homepage,
      near_booth: data.nearBooth,
      image: uploadedCircleImage?.id || imageInfo?.id || 0,
      goods_list:
        uploadedGoodsImageList.length > 0 ? uploadedGoodsImageList : goodsList,
    }

    userCircleBoothformPatchMutate(
      { userBoothApplication },
      {
        onSuccess: (res) => {
          if (res.errorCode < 0) {
            enqueueSnackbar(res.errorMsg, { variant: 'error' })
          } else if (res.errorCode === 0) {
            navigate(`/circle-participation/apply-list/${applicationId}`)
          }
        },
        onError: (error) => {
          enqueueSnackbar(error.message, { variant: 'error' })
        },
      },
    )
  }

  const SelectObjectMultiple = (
    selected: ItemType,
    typeList: ItemType[],
    key: string,
  ) => {
    if (
      typeList.findIndex(
        (object: ItemType) => object.code === selected.code,
      ) === -1
    ) {
      setValue(key as keyof IForm, [...typeList, selected])
    } else {
      setValue(
        key as keyof IForm,
        typeList.filter((object: ItemType) => object.code !== selected.code),
      )
    }
  }

  const checkKeyDown: KeyboardEventHandler<
    HTMLFormElement | HTMLTextAreaElement
  > = (e) => {
    const target = e.target as HTMLElement
    // textarea 안에서는 Enter 키 입력을 허용
    if (e.key === 'Enter' && target.tagName.toLowerCase() !== 'textarea') {
      e.preventDefault()
    }
  }

  useEffect(() => {
    refetch()
  }, [ongoingEvent])

  useEffect(() => {
    if (circleBoothApplyDetail) {
      setValue(
        'sizeType',
        circleBoothApplyDetail.data.userBoothApplication.size_type,
      )
      setValue(
        'complexityType',
        circleBoothApplyDetail.data.userBoothApplication.complexity_type,
      )
      setValue(
        'expType',
        circleBoothApplyDetail.data.userBoothApplication.exp_type,
      )
      setValue(
        'goodsTypeList',
        circleBoothApplyDetail.data.userBoothApplication.goods_type_list,
      )
      setValue(
        'genreTypeList',
        circleBoothApplyDetail.data.userBoothApplication.genre_type_list,
      )
      setValue(
        'genreTags',
        circleBoothApplyDetail.data.userBoothApplication.tag?.split(',') || [],
      )
      setValue(
        'goodsList',
        circleBoothApplyDetail.data.userBoothApplication.goods_list,
      )
      setValue(
        'imageInfo',
        circleBoothApplyDetail.data.userBoothApplication.image_info,
      )
    }
  }, [circleBoothApplyDetail])

  return (
    <Wrapper>
      <BackBox onClick={() => navigate('/circle-participation/info')}>
        <Back />
        <span className="text">참가안내로 돌아가기</span>
      </BackBox>

      {
        <Container>
          <Title>서클 추가 정보 입력</Title>
          <FestivalInfoBox>
            <SubTitle>{ongoingEvent?.data.eventInfo.name}</SubTitle>
            <TagBox>
              <Tag $size="sm" $variant="gray">
                개최 장소
              </Tag>
              <div>{ongoingEvent?.data.eventInfo.place}</div>
            </TagBox>
            <TagBox>
              <Tag $size="sm" $variant="gray">
                개최일
              </Tag>
              <div>
                {ongoingEvent?.data.eventInfo.start_date.slice(0, 11)} ~{' '}
                {ongoingEvent?.data.eventInfo.end_date.slice(0, 11)}
              </div>
            </TagBox>
          </FestivalInfoBox>
          <CircleAddInfoBox
            encType="multipart/form-data"
            onSubmit={handleSubmit(onSubmit)}
            onKeyDown={(e) => checkKeyDown(e)}
          >
            <SubTitle>서클 추가 정보</SubTitle>
            <InputBox className="center">
              <div className="title">인접 희망 서클 이름</div>
              <input
                type="text"
                {...register('nearBooth')}
                defaultValue={
                  circleBoothApplyDetail?.data.userBoothApplication.near_booth
                }
              />
            </InputBox>
            <InputBox>
              <div className="title">서클 대표 이미지 등록</div>
              <FileLabel htmlFor="file">
                <ImageUpload />
                이미지 등록
                <FileInput
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files) {
                      const file = e.target.files[0]
                      uploadCircleImageToServer(file)
                    }
                  }}
                />
              </FileLabel>

              {uploadedCircleImage?.url ? (
                <img
                  className="thumbnail"
                  src={uploadedCircleImage.url}
                  alt="프로필 이미지"
                />
              ) : (
                <img
                  className="thumbnail"
                  src={imageInfo?.url}
                  alt="프로필 이미지"
                />
              )}
            </InputBox>
            <Desc>*300x300px 권장, jpg/png의 2MB 이하 이미지</Desc>
            <InputBox className="center">
              <div className="title">서클 규모</div>
              <SelectButtonBox>
                {circleBoothform &&
                  circleBoothform?.data.boothApplication &&
                  circleBoothform?.data.boothApplication.size_type_list?.map(
                    (size) => (
                      <SelectButton
                        key={size.code}
                        className={size.code === sizeType ? 'active' : ''}
                        onClick={() =>
                          setValue(
                            'sizeType',
                            size.code as keyof typeof SizeType,
                          )
                        }
                      >
                        {size.code === sizeType ? (
                          <Check
                            color={theme.colors.white}
                            width={21}
                            height={21}
                          />
                        ) : null}
                        {size.description}
                      </SelectButton>
                    ),
                  )}
              </SelectButtonBox>
            </InputBox>
            <Desc>
              *부스에 상주하는 인원의 규모가 아닌, 서클에 출품된 작품에 함께한
              인원수를 반영해주세요.
            </Desc>
            <InputBox className="center">
              <div className="title">서클 인파 예상</div>
              <SelectButtonBox>
                {circleBoothform?.data.boothApplication &&
                  circleBoothform?.data.boothApplication.complexity_type_list?.map(
                    (complexity) => (
                      <SelectButton
                        key={complexity.code}
                        className={
                          complexity.code === complexityType ? 'active' : ''
                        }
                        onClick={() =>
                          setValue(
                            'complexityType',
                            complexity.code as keyof typeof ComplexityType,
                          )
                        }
                      >
                        {complexity.code === complexityType ? (
                          <Check
                            color={theme.colors.white}
                            width={21}
                            height={21}
                          />
                        ) : null}
                        {complexity.description}
                      </SelectButton>
                    ),
                  )}
              </SelectButtonBox>
            </InputBox>
            <Desc>
              *최근 참여한 행사 5회 이내에, 부스 앞 대기열이 발생한 적이 있다면
              체크해주세요.
            </Desc>
            <InputBox>
              <div className="title">동인행사 서클참가 경험</div>
              <SelectButtonBox>
                {circleBoothform?.data.boothApplication &&
                  circleBoothform?.data.boothApplication.exp_type_list?.map(
                    (exp) => (
                      <SelectButton
                        key={exp.code}
                        className={exp.code === expType ? 'active' : ''}
                        onClick={() =>
                          setValue('expType', exp.code as keyof typeof ExpType)
                        }
                      >
                        {exp.code === expType ? (
                          <Check
                            color={theme.colors.white}
                            width={21}
                            height={21}
                          />
                        ) : null}
                        {exp.description}
                      </SelectButton>
                    ),
                  )}
              </SelectButtonBox>
            </InputBox>
            <InputBox>
              <div className="title">취급 굿즈 (복수 선택 가능)</div>
              <SelectButtonBox>
                {goodsTypeList &&
                  circleBoothform?.data.boothApplication &&
                  circleBoothform?.data.boothApplication.goods_type_list.map(
                    (goods: ItemType) => {
                      return (
                        <SelectButton
                          key={goods.code}
                          className={
                            goodsTypeList?.findIndex((selectedGoods) => {
                              return goods.code === selectedGoods.code
                            }) !== -1
                              ? 'active'
                              : ''
                          }
                          onClick={() =>
                            SelectObjectMultiple(
                              goods,
                              goodsTypeList,
                              'goodsTypeList',
                            )
                          }
                        >
                          {goodsTypeList?.findIndex(
                            (selectedGoods) =>
                              goods.code === selectedGoods.code,
                          ) !== -1 ? (
                            <Check
                              color={theme.colors.white}
                              width={21}
                              height={21}
                            />
                          ) : null}
                          {goods.description}
                        </SelectButton>
                      )
                    },
                  )}
              </SelectButtonBox>
            </InputBox>
            <InputBox>
              <div className="title">장르 대분류 (복수 선택 가능)</div>
              <SelectButtonBox>
                {genreTypeList &&
                  circleBoothform?.data.boothApplication &&
                  circleBoothform?.data.boothApplication.genre_type_list?.map(
                    (genre: ItemType) => {
                      return (
                        <SelectButton
                          key={genre.code}
                          className={
                            genreTypeList.findIndex(
                              (selectedGenre) =>
                                genre.code === selectedGenre.code,
                            ) !== -1
                              ? 'active'
                              : ''
                          }
                          onClick={() =>
                            SelectObjectMultiple(
                              genre,
                              genreTypeList,
                              'genreTypeList',
                            )
                          }
                        >
                          {genreTypeList.findIndex(
                            (selectedGenre) =>
                              genre.code === selectedGenre.code,
                          ) !== -1 ? (
                            <Check
                              color={theme.colors.white}
                              width={21}
                              height={21}
                            />
                          ) : null}
                          {genre.description}
                        </SelectButton>
                      )
                    },
                  )}
              </SelectButtonBox>
            </InputBox>
            <InputBox className="center">
              <div className="title">세부 장르 태그</div>
              <GenreTag
                genreTags={genreTags}
                setGenreTags={(tags) => setValue('genreTags', tags)}
              />
            </InputBox>
            <InputBox className="center">
              <div className="title">대표자 닉네임</div>
              <input
                type="text"
                {...register('representationNickname')}
                defaultValue={
                  circleBoothApplyDetail?.data.userBoothApplication
                    .user_nickname
                }
              />
            </InputBox>
            <InputBox>
              <div className="title">서클 소개</div>
              <textarea
                {...register('introduce')}
                defaultValue={
                  circleBoothApplyDetail?.data.userBoothApplication.introduce
                }
                onKeyDown={(e) =>
                  checkKeyDown(e as React.KeyboardEvent<HTMLTextAreaElement>)
                }
              />
            </InputBox>
            <InputBox className="center">
              <div className="title">홍보 페이지 url</div>
              <input
                type="text"
                {...register('homepage')}
                defaultValue={
                  circleBoothApplyDetail?.data.userBoothApplication.homepage
                }
              />
            </InputBox>
            <InputBox>
              <div className="title">부스 인포</div>

              <FileLabel htmlFor="file">
                <ImageUpload />
                이미지 등록
                <FileInput
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files) {
                      const file = e.target.files[0]
                      uploadGoodsImageToServer(file)
                    }
                  }}
                />
              </FileLabel>
              {uploadedGoodsImageList.length > 0 ? (
                <>
                  {uploadedGoodsImageList?.map((goods: any) => {
                    return (
                      <img
                        key={goods.id}
                        className="thumbnail"
                        src={goods.url}
                        alt="부스 인포"
                      />
                    )
                  })}
                </>
              ) : (
                <>
                  {goodsList?.map((goods: any) => {
                    return (
                      <img
                        key={goods.id}
                        className="thumbnail"
                        src={goods.url}
                        alt="부스 인포"
                      />
                    )
                  })}
                </>
              )}
            </InputBox>
            <Desc>*jpg/png의 5MB 이하 이미지</Desc>
            <ButtonBox>
              <SubmitButton type="submit" value="등록 완료" />
              <StyledButton
                $variant="transparent"
                $width={253}
                $height={58}
                $fontSize={24}
                onClick={() => navigate(-1)}
              >
                취소하기
              </StyledButton>
            </ButtonBox>
          </CircleAddInfoBox>
        </Container>
      }
    </Wrapper>
  )
}

export default CircleApplyAddInfoPage

const Wrapper = styled.section<any>`
  display: flex;
  flex-direction: column;
  gap: 60px;

  width: 100vw;
  max-width: 1080px;
  min-height: calc(100vh - ${theme.size.headerHeight}px);
  margin: 50px 0;
  margin: 0 auto;
  padding-top: ${theme.size.headerHeight}px;

  @media ${theme.device.medium} {
    gap: 40px;
    width: 90vw;
  }
`
const BackBox = styled.div`
  cursor: pointer;

  display: flex;
  gap: 12px;
  align-items: center;

  margin-top: 20px;

  .text {
    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 21px;
    color: ${theme.colors.grey_4};
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 60px;
  margin: 50px 0;

  @media ${theme.device.medium} {
    gap: 24px;
    margin: 0;
  }
`

const Title = styled.h2`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;

  @media ${theme.device.medium} {
    font-size: 28px;
  }
`

const FestivalInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  height: auto;
  padding: 36px 24px;

  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;
`

const SubTitle = styled.h3`
  margin-bottom: 16px;
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 32px;
  line-height: 44.8px;

  @media ${theme.device.medium} {
    font-size: 21px;
    line-height: 29px;
  }
`

const TagBox = styled.div`
  display: flex;
  gap: 36px;
  align-items: center;
  font-family: NanumSquareNeo-Regular, sans-serif;

  .admin-memo {
    color: ${theme.colors.warning};
  }

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: start;
  }
`

const CircleAddInfoBox = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;

  height: auto;
  padding: 36px 24px;

  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;
`

const InputBox = styled.div`
  overflow: scroll;
  display: flex;
  gap: 20px;

  &.center {
    align-items: center;

    @media ${theme.device.medium} {
      align-items: start;
    }
  }

  .title {
    min-width: 180px;

    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 21px;
    color: ${theme.colors.grey_4};
    text-align: left;

    @media ${theme.device.medium} {
      width: 100%;
    }
  }

  input[type='text'] {
    box-sizing: border-box;
    min-width: 360px;
    height: 49px;
    padding: 20px;

    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;

    background-color: ${theme.colors.grey_1};
    border: none;
    border-radius: 12px;

    @media ${theme.device.medium} {
      width: 100%;
      min-width: 100%;
    }
  }

  textarea {
    box-sizing: border-box;
    width: 100%;
    height: auto;
    min-height: 100px;
    padding: 20px;

    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;

    background-color: ${theme.colors.grey_1};
    border: none;
    border-radius: 12px;

    @media ${theme.device.medium} {
      width: 100%;
    }
  }

  .thumbnail {
    width: 132px;
    height: 132px;
    object-fit: cover;
    border-radius: 10px;
  }

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: start;
  }
`

const Desc = styled.div`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 21px;
  color: ${theme.colors.grey_4};
`

const FileLabel = styled.label`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;

  width: 132px;
  min-width: 132px;
  height: 132px;

  font-weight: 500;

  background: ${theme.colors.grey_1};
  border-radius: 10px;
`

const FileInput = styled.input`
  cursor: pointer;

  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  min-width: 132px;
  height: 100%;

  opacity: 0;
`

const SelectButtonBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`

const SelectButton = styled.span`
  cursor: pointer;

  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: fit-content;
  height: 49px;
  padding: 12px 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;

  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;

  &.active {
    color: ${theme.colors.white};
    background-color: ${theme.colors.mint_3};
    border: none;
  }

  @media ${theme.device.medium} {
    width: 100%;
    font-size: 15px;
  }
`

const ButtonBox = styled.div`
  display: flex;
  gap: 20px;
`

const SubmitButton = styled.input`
  cursor: pointer;

  gap: 4px;

  width: 148px;
  height: 58px;
  padding: 8px 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 24px;
  font-weight: 400;
  color: ${theme.colors.white};

  background-color: ${theme.colors.grey_5};
  border: none;
  border-radius: 50px;

  @media ${theme.device.medium} {
    width: 100%;
    font-size: 15px;
  }
`

const StyledButton = styled(Button)`
  width: fit-content;
  font-size: 24px;

  @media ${theme.device.medium} {
    width: 100%;
    font-size: 18px;
  }
`
